/**
 * 国优炬康 后台 8 个业务域、38 个二级菜单配置
 *
 * G5-DEV-016 — 后台 8 业务域 × 50 二级菜单 + 越权页
 *
 * 详见:packages/shared/src/permissions/index.ts
 */

import type { AdminPermission } from '@gyjk/shared';

/**
 * 一级业务域(8 个)
 */
export interface MenuModule {
  id: string;
  title: string;
  icon: string;
  order: number;
  children: MenuItem[];
}

/**
 * 二级菜单项(50 个)
 */
export interface MenuItem {
  id: string;
  title: string;
  path: string;
  icon?: string;
  permission: `${AdminPermission}`;
  order: number;
  badge?: string | number;
}

export const MENU_MODULES: MenuModule[] = [
  {
    id: 'dashboard',
    title: '工作台',
    icon: 'House',
    order: 1,
    children: [
      { id: 'dashboard-home', title: '工作台', path: '/dashboard', icon: 'DataLine', permission: 'DASHBOARD_VIEW', order: 1 },
      { id: 'dashboard-switch-role', title: '角色切换', path: '/dashboard/switch-role', icon: 'Refresh', permission: 'DASHBOARD_SWITCH_ROLE', order: 3 },
    ],
  },
  {
    id: 'content',
    title: '内容运营',
    icon: 'Document',
    order: 2,
    children: [
      { id: 'content-review', title: '审核中心', path: '/content/review', icon: 'Stamp', permission: 'CONTENT_REVIEW_LIST', order: 1 },
      { id: 'content-banner', title: '轮播图管理', path: '/content/banner', icon: 'Picture', permission: 'CONTENT_BANNER_LIST', order: 1 },
      { id: 'content-home-config', title: '首页配置', path: '/content/home-config', icon: 'Grid', permission: 'CONTENT_ARTICLE_EDIT', order: 1 },
      { id: 'content-category', title: '内容分类', path: '/content/category', icon: 'Menu', permission: 'CONTENT_ARTICLE_EDIT', order: 2 },
      { id: 'content-article', title: '文章管理', path: '/content/article', icon: 'Notebook', permission: 'CONTENT_ARTICLE_LIST', order: 3 },
      { id: 'content-video', title: '科普视频', path: '/content/video', icon: 'VideoPlay', permission: 'CONTENT_ARTICLE_LIST', order: 4 },
      { id: 'content-expert', title: '专家管理', path: '/content/expert', icon: 'User', permission: 'CONTENT_EXPERT_LIST', order: 4 },
      { id: 'content-health-coach', title: '健康管理师', path: '/content/health-coach', icon: 'User', permission: 'CONTENT_HEALTH_COACH_LIST', order: 4 },
      { id: 'content-consult', title: '咨询管理', path: '/content/consult', icon: 'ChatLineRound', permission: 'CONTENT_CONSULT_LIST', order: 4 },
      { id: 'content-square', title: '生活广场', path: '/content/square', icon: 'Grid', permission: 'CONTENT_SQUARE_LIST', order: 5 },
      { id: 'content-live', title: '直播管理', path: '/content/live', icon: 'VideoPlay', permission: 'CONTENT_LIVE_LIST', order: 6 },
    ],
  },
  {
    id: 'member',
    title: '会员',
    icon: 'UserFilled',
    order: 3,
    children: [
      { id: 'member-list', title: '会员列表', path: '/member/list', icon: 'List', permission: 'MEMBER_LIST', order: 1 },
      { id: 'member-level', title: '会员等级', path: '/member/level', icon: 'Trophy', permission: 'MEMBER_LEVEL_EDIT', order: 2 },
      { id: 'member-family', title: '家庭成员', path: '/member/family', icon: 'UserFilled', permission: 'MEMBER_FAMILY_LIST', order: 2 },
      { id: 'member-points', title: '积分管理', path: '/member/points', icon: 'Star', permission: 'MEMBER_POINTS_EDIT', order: 3 },
      { id: 'member-service-package', title: '服务包配置', path: '/member/service-package', icon: 'Box', permission: 'MEMBER_LEVEL_EDIT', order: 3 },
      { id: 'member-points-config', title: '积分汇率', path: '/member/points-config', icon: 'Money', permission: 'MEMBER_POINTS_EDIT', order: 4 },
      { id: 'member-badge', title: '勋章管理', path: '/member/badge', icon: 'Medal', permission: 'MEMBER_BADGE_EDIT', order: 5 },
      { id: 'member-distribution-config', title: '分销配置', path: '/member/distribution-config', icon: 'SetUp', permission: 'FIN_COMMISSION_LIST', order: 6 },
    ],
  },
  {
    id: 'goods',
    title: '商品',
    icon: 'Goods',
    order: 4,
    children: [
      { id: 'goods-category', title: '类目管理', path: '/goods/category', icon: 'Menu', permission: 'GOODS_CATEGORY_LIST', order: 1 },
      { id: 'goods-product', title: '商品列表', path: '/goods/product', icon: 'List', permission: 'GOODS_PRODUCT_LIST', order: 2 },
      { id: 'goods-shipping-template', title: '运费模板', path: '/goods/shipping-template', icon: 'Van', permission: 'GOODS_SHIPPING_TEMPLATE_LIST', order: 3 },
      { id: 'goods-stock', title: '库存流水', path: '/goods/stock', icon: 'Box', permission: 'GOODS_STOCK_LIST', order: 4 },
      { id: 'goods-review', title: '商品评价', path: '/goods/review', icon: 'Star', permission: 'GOODS_REVIEW_LIST', order: 5 },
    ],
  },
  {
    id: 'order',
    title: '订单',
    icon: 'List',
    order: 5,
    children: [
      { id: 'order-list', title: '订单列表', path: '/order/list', icon: 'Tickets', permission: 'ORDER_LIST', order: 1 },
      { id: 'order-refund', title: '退款审核', path: '/order/refund', icon: 'RefreshLeft', permission: 'ORDER_REFUND_LIST', order: 3 },
    ],
  },
  {
    id: 'finance',
    title: '财务',
    icon: 'Money',
    order: 6,
    children: [
      { id: 'finance-payment', title: '支付流水', path: '/finance/payment', icon: 'CreditCard', permission: 'FIN_PAYMENT_LIST', order: 1 },
      { id: 'finance-refund', title: '退款流水', path: '/finance/refund', icon: 'RefreshLeft', permission: 'FIN_REFUND_LIST', order: 2 },
      { id: 'finance-relation', title: '分销关系', path: '/finance/relation', icon: 'Connection', permission: 'FIN_COMMISSION_LIST', order: 3 },
      { id: 'finance-commission', title: '佣金流水', path: '/finance/commission', icon: 'Coin', permission: 'FIN_COMMISSION_LIST', order: 5 },
      { id: 'finance-withdrawal', title: '提现审核', path: '/finance/withdrawal', icon: 'Wallet', permission: 'FIN_WITHDRAWAL_LIST', order: 7 },
    ],
  },
  {
    id: 'health',
    title: '健康',
    icon: 'FirstAidKit',
    order: 7,
    children: [
      { id: 'health-record', title: '健康记录', path: '/health/record', icon: 'Document', permission: 'HEALTH_RECORD_LIST', order: 1 },
      { id: 'health-consent', title: '健康授权', path: '/health/consent', icon: 'Lock', permission: 'HEALTH_CONSENT_LIST', order: 2 },
      { id: 'health-device', title: '设备管理', path: '/health/device', icon: 'Cellphone', permission: 'HEALTH_DEVICE_LIST', order: 3 },
      { id: 'health-diet-ai', title: '饮食 AI', path: '/health/diet-ai', icon: 'Food', permission: 'HEALTH_DIET_AI_LIST', order: 4 },
      { id: 'health-volunteer', title: '志愿者管理', path: '/health/volunteer', icon: 'UserFilled', permission: 'HEALTH_VOLUNTEER_LIST', order: 5 },
      { id: 'health-recipe', title: '食谱管理', path: '/health/recipe', icon: 'Notebook', permission: 'HEALTH_RECIPE_LIST', order: 6 },
      { id: 'health-food', title: '食物库', path: '/health/food', icon: 'Food', permission: 'HEALTH_FOOD_LIST', order: 7 },
      { id: 'health-alert', title: '健康预警', path: '/health/alert', icon: 'Bell', permission: 'HEALTH_ALERT_LIST', order: 8 },
      { id: 'health-patient', title: '我的患者', path: '/health/patient', icon: 'Link', permission: 'HEALTH_PATIENT_LIST', order: 9 },
      { id: 'health-member-health', title: '会员健康', path: '/health/member-health', icon: 'User', permission: 'HEALTH_MEMBER_HEALTH_VIEW', order: 10 },
    ],
  },
  {
    id: 'system',
    title: '系统',
    icon: 'Setting',
    order: 8,
    children: [
      { id: 'system-admin', title: '管理员', path: '/system/admin', icon: 'UserFilled', permission: 'SYS_ADMIN_LIST', order: 1 },
      { id: 'system-role', title: '角色权限', path: '/system/role', icon: 'Key', permission: 'SYS_ROLE_EDIT', order: 2 },
      { id: 'system-audit-log', title: '审计日志', path: '/system/audit-log', icon: 'Document', permission: 'SYS_AUDIT_LOG', order: 3 },
      { id: 'system-pages', title: '协议管理', path: '/system/pages', icon: 'Document', permission: 'SYS_PAGE_EDIT', order: 5 },
      { id: 'system-service', title: '客服配置', path: '/system/service', icon: 'Service', permission: 'SYS_THIRD_PARTY_CONFIG', order: 6 },
      { id: 'system-brand', title: '品牌与分享', path: '/system/brand', icon: 'MagicStick', permission: 'SYS_BRAND_CONFIG', order: 7 },
    ],
  },
];

/**
 * 统计当前二级菜单数量
 */
export const TOTAL_MENU_ITEMS = MENU_MODULES.reduce((sum, m) => sum + m.children.length, 0);
